/**
 * Storage-related types and interfaces
 */

import { ParsedChartData } from './types';
import { ChartStateSnapshot } from '../types/MultipleCharts';

// Chart datetime information extracted from JHora data
export interface ChartDateTime {
  date: string;           // YYYY-MM-DD format
  time: string;           // HH:mm:ss format
  timezone: string;       // Timezone string (e.g., "+03:00")
  location?: string;      // Location name if available
  originalDateStr?: string; // Original date string from import
  originalTimeStr?: string; // Original time string from import
}

// Chart metadata for storage
export interface ChartMetadata {
  id: string;             // Unique identifier (timestamp-based)
  name: string;           // User-friendly name
  created: Date;          // When chart was first saved
  modified: Date;         // Last modification time
  accessed: Date;         // Last access time
  size: number;           // Storage size in bytes
  version: number;        // Schema version for migrations
  tags?: string[];        // Optional tags for organization
  description?: string;   // Optional description
  isFavorite?: boolean;   // Favorite status for chart management
  chartDateTime?: ChartDateTime; // Extracted datetime info
  shortLinks?: string[];  // Array of short link codes if chart has been shared (e.g., ["ABC12345", "XYZ67890"])
  contentHash?: string;   // Hash of chart content (location+datetime+timezone) for duplicate detection
  // Soft delete fields
  isDeleted?: boolean;    // Flag indicating item is marked for deletion
  deletedAt?: Date;       // When item was marked for deletion
  permanentDeleteAt?: Date; // When item will be permanently deleted
}

// Complete saved chart with data and metadata
export interface SavedChart {
  metadata: ChartMetadata;
  chartData: ParsedChartData;
  history?: ChartStateSnapshot[];  // Chart state history for undo/navigation
  currentHistoryIndex?: number;    // Current active history index (CURRENT_LIVE_INDEX = last)
}

// Storage service configuration
export interface StorageConfig {
  databaseName: string;
  version: number;
  maxRecentFiles: number;
  compressionEnabled: boolean;
  autoSave: boolean;
}

// Storage operation results
export interface StorageResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: StorageErrorCode;
}

// Storage error codes for specific error handling
export enum StorageErrorCode {
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  NOT_FOUND = 'NOT_FOUND',
  DUPLICATE_NAME = 'DUPLICATE_NAME',
  INVALID_DATA = 'INVALID_DATA',
  STORAGE_UNAVAILABLE = 'STORAGE_UNAVAILABLE',
  MIGRATION_FAILED = 'MIGRATION_FAILED',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED'
}

// Query options for listing charts
export interface ListChartsOptions {
  sortBy?: 'name' | 'created' | 'modified' | 'accessed';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  searchTerm?: string;
  tags?: string[];
}

// Export options for chart data
export interface ExportOptions {
  format: 'json' | 'csv' | 'jhora';
  includeMetadata: boolean;
  prettify?: boolean;
  compression?: boolean;
}

// Import validation result
export interface ImportValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  extractedDateTime?: ChartDateTime;
}
